import { useTranslation } from 'react-i18next';
import { DownloadSimpleIcon, TrashIcon, FileTextIcon, XIcon, WarningIcon } from '@phosphor-icons/react';
import { Input, Pills } from 'barte-design-system';

import { DropZone } from '@/components/DropZone';

import styles from './step-terminals.module.css';

import { MAX_SERIALS_PER_BATCH } from '../../terminal-operation-drawer.utils';
import type { IStepTerminalsProps } from './step-terminals.type';

const buildRegisterTemplateCsv = () =>
  ['serial_number', 'PB1S248P70602', 'PB1S248P70533', 'PB1S248S76189'].join('\n');

const buildLinkTemplateCsv = () =>
  ['serial_number;payfac_id;seller_id', 'PB1S248P70602;1;', 'PB1S248P70533;;2', 'PB1S248S76189;1;3'].join('\n');

const buildUnlinkTemplateCsv = () =>
  ['serial_number', 'PB1S248P70602', 'PB1S248P70533', 'PB1S248S76189'].join('\n');

const handleDownloadTemplate = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const renderIdCell = (id: number | null | undefined, name: string | null | undefined, styles: Record<string, string>) => {
  if (id == null) return '—';
  return (
    <>
      <span className={styles['preview-cell-id']}>{id}</span>
      {name && <span className={styles['preview-cell-name']}>{name}</span>}
    </>
  );
};

export const StepTerminals = ({
  operationType,
  selectedModel,
  serialNumbers,
  csvFile,
  csvBatchRows,
  isResolvingNames,
  isDragging,
  isSubmitting,
  fileInputRef,
  serialInputProps,
  serialError,
  onAddSerial,
  onRemoveSerial,
  onRemoveCsvFile,
  onRemoveCsvRow,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileChange,
}: IStepTerminalsProps) => {
  const { t } = useTranslation();

  const isLinkOperation = operationType === 'LINK';

  const getCsvTemplateAction = () => {
    if (isLinkOperation) {
      return () => handleDownloadTemplate(buildLinkTemplateCsv(), 'modelo-vinculacao-terminais.csv');
    }
    if (operationType === 'UNLINK') {
      return () => handleDownloadTemplate(buildUnlinkTemplateCsv(), 'modelo-desvinculacao-terminais.csv');
    }
    return () => handleDownloadTemplate(buildRegisterTemplateCsv(), 'modelo-cadastro-terminais.csv');
  };

  const importDescriptionKey = isLinkOperation
    ? 'backoffice.terminalsDrawerStepTerminalsImportDescriptionLink'
    : 'backoffice.terminalsDrawerStepTerminalsImportDescription';

  return (
    <div className={styles.container}>
      {selectedModel && (
        <Pills
          label={`${t('backoffice.terminalsDrawerStepModelChipLabel')}: ${selectedModel.name}`}
          variant="light"
          state="default"
          size="sm"
        />
      )}

      <div className={styles.section}>
        <span className={styles['section-title']}>
          {t('backoffice.terminalsDrawerStepTerminalsImportTitle')}
        </span>
        <span className={styles['section-description']}>
          {t(importDescriptionKey)}
        </span>

        {csvFile ? (
          <div className={styles['csv-file-chip']}>
            <FileTextIcon size={14} weight="regular" className={styles['csv-file-icon']} />
            <span className={styles['csv-file-name']}>{csvFile.name}</span>
            <button
              type="button"
              className={styles['csv-file-remove']}
              onClick={onRemoveCsvFile}
              disabled={isSubmitting}
              aria-label={t('backoffice.terminalsDrawerRemoveCsvFile')}
            >
              <XIcon size={12} weight="bold" />
            </button>
          </div>
        ) : (
          <DropZone
            isDragging={isDragging}
            fileInputRef={fileInputRef}
            accept=".csv,.xlsx"
            title={t('backoffice.terminalsDrawerStepTerminalsDragTitle')}
            hint={t('backoffice.terminalsDrawerStepTerminalsHint')}
            selectFileLabel={t('backoffice.terminalsDrawerStepTerminalsSelectFile')}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onFileChange={onFileChange}
          />
        )}

        <div className={styles['drop-zone-actions']}>
          <button type="button" className={styles['action-link']} onClick={getCsvTemplateAction()}>
            <DownloadSimpleIcon size={14} />
            {t('backoffice.terminalsDrawerStepTerminalsDownloadTemplate')}
          </button>
        </div>
      </div>

      <div className={styles.separator}>
        <span className={styles['separator-line']} />
        <span className={styles['separator-text']}>
          {t('backoffice.terminalsDrawerStepTerminalsSeparator')}
        </span>
        <span className={styles['separator-line']} />
      </div>

      <div className={styles.section}>
        <span className={styles['section-title']}>
          {t('backoffice.terminalsDrawerStepTerminalsManualTitle')}
        </span>
        <span className={styles['section-description']}>
          {t('backoffice.terminalsDrawerStepTerminalsManualDescription')}
        </span>

        <form onSubmit={onAddSerial} className={styles['input-row']}>
          <Input
            {...serialInputProps}
            placeholder={t('backoffice.terminalsDrawerStepTerminalsSerialPlaceholder')}
            error={serialError}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={isSubmitting}
            className={styles['serial-input']}
          />
          <button
            type="submit"
            className={styles['add-button']}
            disabled={isSubmitting}
          >
            + {t('backoffice.terminalsDrawerStepTerminalsAddButton')}
          </button>
        </form>
      </div>

      {serialNumbers.length > 0 && (
        <div className={styles['counter-row']}>
          {serialNumbers.length > MAX_SERIALS_PER_BATCH ? (
            <Pills
              label={t('backoffice.terminalsDrawerStepTerminalsOverLimit')}
              variant="light"
              state="error"
              size="sm"
            />
          ) : (
            <Pills
              label={`${serialNumbers.length} / ${MAX_SERIALS_PER_BATCH} ${t('backoffice.terminalsDrawerStepTerminalsCounterLabel')}`}
              variant="ghost"
              state="default"
              size="sm"
              className={styles['counter-pill']}
            />
          )}
        </div>
      )}

      {serialNumbers.length > 0 && (
        <div className={styles['serial-table']}>
          <div className={styles['serial-table-header']}>
            <span>{t('backoffice.terminalsDrawerStepTerminalsTableHeader')}</span>
            {selectedModel && (
              <span>{t('backoffice.terminalsDrawerStepTerminalsTableHeaderModel')}</span>
            )}
          </div>
          {serialNumbers.map((serial) => (
            <div key={serial} className={styles['serial-table-row']}>
              <span className={styles['serial-value']}>{serial}</span>
              {selectedModel && <span className={styles['serial-model']}>{selectedModel.name}</span>
              }
              <button
                type="button"
                className={styles['remove-button']}
                onClick={() => onRemoveSerial(serial)}
                disabled={isSubmitting}
              >
                <TrashIcon size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      {isLinkOperation && (
        <>
          {isResolvingNames && (
            <div className={styles['resolve-loading']}>
              <span className={styles['resolve-spinner']} />
              <span>{t('backoffice.terminalsDrawerResolvingNames')}</span>
            </div>
          )}

          {!isResolvingNames && csvBatchRows.length > 0 && (
            <div className={styles['preview-counter-row']}>
              <Pills
                label={`${csvBatchRows.length} / ${MAX_SERIALS_PER_BATCH} ${t('backoffice.terminalsDrawerStepTerminalsCounterLabel')}`}
                variant="ghost"
                state="default"
                size="sm"
                className={styles['counter-pill']}
              />
            </div>
          )}

          {!isResolvingNames && csvBatchRows.length > 0 && (
            <div className={styles['preview-table']}>
              <div className={styles['preview-table-header']}>
                <span>{t('backoffice.terminalsDrawerResultColumnSerial')}</span>
                <span>{t('backoffice.terminalsDrawerPreviewColumnPayfac')}</span>
                <span>{t('backoffice.terminalsDrawerPreviewColumnSeller')}</span>
                <span />
              </div>
              {csvBatchRows.map((row) => {
                const hasWarning = row.payfacId == null;
                return (
                  <div key={row.serialNumber} className={styles['preview-table-row']}>
                    <span className={styles['preview-serial-cell']}>
                      {hasWarning && (
                        <WarningIcon size={14} weight="fill" className={styles['preview-warning-icon']} />
                      )}
                      <span className={styles['preview-serial']}>{row.serialNumber}</span>
                    </span>

                    <span className={styles['preview-cell-two-line']}>
                      {renderIdCell(row.payfacId, row.payfacName, styles)}
                    </span>

                    <span className={styles['preview-cell-two-line']}>
                      {renderIdCell(row.sellerId, row.sellerName, styles)}
                    </span>

                    <button
                      type="button"
                      className={styles['preview-remove-button']}
                      onClick={() => onRemoveCsvRow(row.serialNumber)}
                      disabled={isSubmitting}
                    >
                      <TrashIcon size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {operationType === 'UNLINK' && csvBatchRows.length > 0 && (
        <>
          <div className={styles['counter-row']}>
            <Pills
              label={`${csvBatchRows.length} / ${MAX_SERIALS_PER_BATCH} ${t('backoffice.terminalsDrawerStepTerminalsCounterLabel')}`}
              variant="ghost"
              state="default"
              size="sm"
              className={styles['counter-pill']}
            />
          </div>
          <div className={styles['serial-table']}>
            <div className={styles['serial-table-header']}>
              <span>{t('backoffice.terminalsDrawerStepTerminalsTableHeader')}</span>
            </div>
            {csvBatchRows.map((row) => (
              <div key={row.serialNumber} className={styles['serial-table-row']}>
                <span className={styles['serial-value']}>{row.serialNumber}</span>
                <span />
                <button
                  type="button"
                  className={styles['remove-button']}
                  onClick={() => onRemoveCsvRow(row.serialNumber)}
                  disabled={isSubmitting}
                >
                  <TrashIcon size={15} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
